import Typography from "@material-ui/core/Typography";
import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import CountryList from "@saleor/components/CountryList";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { RequirementsPicker } from "@saleor/discounts/types";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import { sectionNames } from "@saleor/intl";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, splitDateTime } from "../../../misc";
import { ListProps2, TabListActions } from "../../../types";
import {
  DiscountValueTypeEnum,
  VoucherTypeEnum
} from "../../../types/globalTypes";
import { VoucherDetails_voucher } from "../../types/VoucherDetails";
import DiscountCategories from "../DiscountCategories";
import DiscountCollections from "../DiscountCollections";
import DiscountDates from "../DiscountDates";
import DiscountExcludeCategories from "../DiscountExcludeCategories/DiscountExcludeCategories";
import DiscountExcludeCollections from "../DiscountExcludeCollections";
import DiscountExcludeProducts from "../DiscountExcludeProducts";
import DiscountExcludeVariants from "../DiscountExcludeVariants";
import DiscountProducts from "../DiscountProducts";
import DiscountVariants from "../DiscountVariants";
import LanguageChoice from "../LanguageChoice/LanguageChoice";
import VoucherInfo from "../VoucherInfo";
import VoucherLimits from "../VoucherLimits";
import VoucherRequirements from "../VoucherRequirements";
import VoucherSummary from "../VoucherSummary";
import VoucherTypes from "../VoucherTypes";
import VoucherValue from "../VoucherValue";

export enum VoucherDetailsPageTab {
  categories = "categories",
  collections = "collections",
  products = "products",
  variants = "variants"
}

export function voucherDetailsPageTab(tab: string): VoucherDetailsPageTab {
  return tab === VoucherDetailsPageTab.variants
    ? VoucherDetailsPageTab.variants
    : tab === VoucherDetailsPageTab.products
    ? VoucherDetailsPageTab.products
    : tab === VoucherDetailsPageTab.collections
    ? VoucherDetailsPageTab.collections
    : VoucherDetailsPageTab.categories;
}

export interface VoucherDetailsPageFormData {
  applyNumberPerCustomer: string;
  applySpecificCustomer: boolean;
  applyShopAll: boolean;
  isAff: boolean;
  isRepeatable: boolean;
  code: string;
  name: string;
  discountType: DiscountValueTypeEnum;
  endDate: string;
  endTime: string;
  hasApplyNumberPerCustomer: boolean;
  hasEndDate: boolean;
  hasUsageLimit: boolean;
  minSpent: string;
  minCheckoutItemsQuantity: string;
  requirementsPicker: RequirementsPicker;
  startDate: string;
  startTime: string;
  type: VoucherTypeEnum;
  usageLimit: string;
  value: number;
  displayOrder: number;
  descriptionText: string;
  descriptionSubtext: string;
  descriptionThirdtext: string;
  includeDiscountProduct: boolean;
  includeComboProduct: boolean;
  limitTime: number;
  hasLimitTime: boolean;
}

export interface VoucherDetailsPageProps
  extends Pick<ListProps2, Exclude<keyof ListProps2, "onRowClick">>,
    TabListActions<
      | "categoryListToolbar"
      | "collectionListToolbar"
      | "productListToolbar"
      | "variantListToolbar"
      | "excludeCategoryListToolbar"
      | "excludeCollectionListToolbar"
      | "excludeProductListToolbar"
      | "excludeVariantListToolbar"
    > {
  activeTab: VoucherDetailsPageTab;
  defaultCurrency: string;
  errors: DiscountErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  voucher: VoucherDetails_voucher;
  onBack: () => void;
  onCategoryAssign: () => void;
  onCategoryUnassign: (id: string) => void;
  onExcludeCategoryAssign: () => void;
  onExcludeCategoryUnassign: (id: string) => void;
  onCategoryClick: (id: string) => () => void;
  onCollectionAssign: () => void;
  onCollectionUnassign: (id: string) => void;
  onExcludeCollectionAssign: () => void;
  onExcludeCollectionUnassign: (id: string) => void;
  onCollectionClick: (id: string) => () => void;
  onCountryAssign: () => void;
  onCountryUnassign: (code: string) => void;
  onProductAssign: () => void;
  onProductUnassign: (id: string) => void;
  onExcludeProductAssign: () => void;
  onExcludeProductUnassign: (id: string) => void;
  onLanguageAssign: () => void;
  onLanguageUnAssign: (id: string) => void;
  onProductClick: (id: string) => () => void;
  onVariantAssign: () => void;
  onVariantUnassign: (id: string) => void;
  onExcludeVariantAssign: () => void;
  onExcludeVariantUnassign: (id: string) => void;
  onVariantClick: (productId: string, variantId: string) => () => void;
  onRemove: () => void;
  onSubmit: (data: VoucherDetailsPageFormData) => void;
  onTabClick: (index: VoucherDetailsPageTab) => void;
}

const CategoriesTab = Tab(VoucherDetailsPageTab.categories);
const CollectionsTab = Tab(VoucherDetailsPageTab.collections);
const ProductsTab = Tab(VoucherDetailsPageTab.products);
const VariantsTab = Tab(VoucherDetailsPageTab.variants);

const VoucherDetailsPage: React.FC<VoucherDetailsPageProps> = ({
  activeTab,
  defaultCurrency,
  disabled,
  errors,
  pageInfo,
  saveButtonBarState,
  voucher,
  onBack,
  onCategoryAssign,
  onCategoryUnassign,
  onCollectionAssign,
  onCollectionUnassign,
  onProductAssign,
  onProductUnassign,
  onVariantAssign,
  onVariantUnassign,
  onExcludeCategoryAssign,
  onExcludeCategoryUnassign,
  onExcludeCollectionAssign,
  onExcludeCollectionUnassign,
  onExcludeProductAssign,
  onExcludeProductUnassign,
  onExcludeVariantAssign,
  onExcludeVariantUnassign,
  onCategoryClick,
  onLanguageAssign,
  onLanguageUnAssign,
  onCountryAssign,
  onCountryUnassign,
  onCollectionClick,
  onNextPage,
  onPreviousPage,
  onProductClick,
  onVariantClick,
  onTabClick,
  onRemove,
  onSubmit,
  toggle,
  toggleAll,
  selected,
  isChecked,
  categoryListToolbar,
  collectionListToolbar,
  productListToolbar,
  variantListToolbar,
  excludeCategoryListToolbar,
  excludeCollectionListToolbar,
  excludeProductListToolbar,
  excludeVariantListToolbar
}) => {
  const intl = useIntl();

  let requirementsPickerInitValue;
  if (maybe(() => voucher.minSpent.amount) > 0) {
    requirementsPickerInitValue = RequirementsPicker.ORDER;
  } else if (maybe(() => voucher.minCheckoutItemsQuantity) > 0) {
    requirementsPickerInitValue = RequirementsPicker.ITEM;
  } else {
    requirementsPickerInitValue = RequirementsPicker.NONE;
  }

  const initialForm: VoucherDetailsPageFormData = {
    applyNumberPerCustomer: maybe(
      () => voucher.applyNumberPerCustomer.toString(),
      "0"
    ),
    applySpecificCustomer: maybe(() => voucher.applySpecificCustomer, true),
    applyShopAll: maybe(() => voucher.applyShopAll, false),
    isAff: maybe(() => voucher.isAff, false),
    isRepeatable: maybe(() => voucher.isRepeatable, true),
    code: maybe(() => voucher.code, ""),
    name: maybe(() => voucher.name, ""),
    descriptionText: maybe(() => voucher.descriptionText, ""),
    descriptionSubtext: maybe(() => voucher.descriptionSubtext, ""),
    descriptionThirdtext: maybe(() => voucher.descriptionThirdtext, ""),
    discountType:
      voucher?.type === "SHIPPING"
        ? ("SHIPPING" as DiscountValueTypeEnum)
        : maybe(
            () => voucher.discountValueType,
            DiscountValueTypeEnum.PERCENTAGE
          ),
    endDate: splitDateTime(maybe(() => voucher.endDate, "")).date,
    endTime: splitDateTime(maybe(() => voucher.endDate, "")).time,
    hasApplyNumberPerCustomer: maybe(() => !!voucher.applyNumberPerCustomer),
    hasEndDate: maybe(() => !!voucher.endDate),
    hasUsageLimit: maybe(() => !!voucher.usageLimit),
    minCheckoutItemsQuantity: maybe(
      () => voucher.minCheckoutItemsQuantity.toString(),
      "0"
    ),
    minSpent: maybe(() => voucher.minSpent.amount.toString(), "0"),
    requirementsPicker: requirementsPickerInitValue,
    startDate: splitDateTime(maybe(() => voucher.startDate, "")).date,
    startTime: splitDateTime(maybe(() => voucher.startDate, "")).time,
    type: maybe(() => voucher.type, VoucherTypeEnum.ENTIRE_ORDER),
    usageLimit: maybe(() => voucher.usageLimit.toString(), "0"),
    value: maybe(() => voucher.discountValue, 0),
    displayOrder: maybe(() => voucher.displayOrder, 999),
    includeDiscountProduct: maybe(() => voucher.includeDiscountProduct, false),
    includeComboProduct: maybe(() => voucher.includeComboProduct, false),
    limitTime: maybe(() => voucher.limitTime, 0),
    hasLimitTime: maybe(() => voucher.limitTime !== 0)
  };

  return (
    <Form initial={initialForm} onSubmit={onSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.vouchers)}
          </AppHeader>
          <PageHeader title={maybe(() => voucher.code)} />
          <Grid>
            <div>
              <VoucherInfo
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
                variant="update"
              />
              <CardSpacer />
              <VoucherTypes
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
              />
              <CardSpacer />
              {data.discountType.toString() !== "SHIPPING" ? (
                <VoucherValue
                  data={data}
                  disabled={disabled}
                  defaultCurrency={defaultCurrency}
                  errors={errors}
                  onChange={change}
                  variant="update"
                />
              ) : null}
              <CardSpacer />
              {data.type === VoucherTypeEnum.SPECIFIC_PRODUCT &&
              data.discountType.toString() !== "SHIPPING" ? (
                <>
                  <TabContainer>
                    <CategoriesTab
                      isActive={activeTab === VoucherDetailsPageTab.categories}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "Categories ({quantity})",
                          description: "number of categories"
                        },
                        {
                          quantity: maybe(
                            () => voucher.categories.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </CategoriesTab>
                    <CollectionsTab
                      isActive={activeTab === VoucherDetailsPageTab.collections}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "Recommendation Groups ({quantity})",
                          description: "number of collections"
                        },
                        {
                          quantity: maybe(
                            () => voucher.collections.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </CollectionsTab>
                    <ProductsTab
                      isActive={activeTab === VoucherDetailsPageTab.products}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "Products ({quantity})",
                          description: "number of products"
                        },
                        {
                          quantity: maybe(
                            () => voucher.products.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </ProductsTab>
                    <VariantsTab
                      isActive={activeTab === VoucherDetailsPageTab.variants}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "SKU({quantity})",
                          description: "number of skus"
                        },
                        {
                          quantity: maybe(
                            () => voucher.variants.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </VariantsTab>
                  </TabContainer>
                  <CardSpacer />
                  {activeTab === VoucherDetailsPageTab.categories ? (
                    <DiscountCategories
                      disabled={disabled}
                      onCategoryAssign={onCategoryAssign}
                      onCategoryUnassign={onCategoryUnassign}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onRowClick={onCategoryClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={categoryListToolbar}
                    />
                  ) : activeTab === VoucherDetailsPageTab.collections ? (
                    <DiscountCollections
                      disabled={disabled}
                      onCollectionAssign={onCollectionAssign}
                      onCollectionUnassign={onCollectionUnassign}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onRowClick={onCollectionClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={collectionListToolbar}
                    />
                  ) : activeTab === VoucherDetailsPageTab.products ? (
                    <DiscountProducts
                      disabled={disabled}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onProductAssign={onProductAssign}
                      onProductUnassign={onProductUnassign}
                      onRowClick={onProductClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={productListToolbar}
                    />
                  ) : (
                    <DiscountVariants
                      disabled={disabled}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onVariantAssign={onVariantAssign}
                      onVariantUnassign={onVariantUnassign}
                      onRowClick={onVariantClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={variantListToolbar}
                    />
                  )}
                </>
              ) : null}
              {data.type === VoucherTypeEnum.EXCLUDE_PRODUCT &&
              data.discountType.toString() !== "SHIPPING" ? (
                <>
                  <TabContainer>
                    <CategoriesTab
                      isActive={activeTab === VoucherDetailsPageTab.categories}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "Categories ({quantity})",
                          description: "number of categories"
                        },
                        {
                          quantity: maybe(
                            () =>
                              voucher.excludeCategories.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </CategoriesTab>
                    <CollectionsTab
                      isActive={activeTab === VoucherDetailsPageTab.collections}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "Recommendation Groups ({quantity})",
                          description: "number of collections"
                        },
                        {
                          quantity: maybe(
                            () =>
                              voucher.excludeCollections.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </CollectionsTab>
                    <ProductsTab
                      isActive={activeTab === VoucherDetailsPageTab.products}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "Products ({quantity})",
                          description: "number of products"
                        },
                        {
                          quantity: maybe(
                            () => voucher.excludeProducts.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </ProductsTab>
                    <VariantsTab
                      isActive={activeTab === VoucherDetailsPageTab.variants}
                      changeTab={onTabClick}
                    >
                      {intl.formatMessage(
                        {
                          defaultMessage: "SKU({quantity})",
                          description: "number of skus"
                        },
                        {
                          quantity: maybe(
                            () => voucher.excludeVariants.totalCount.toString(),
                            "…"
                          )
                        }
                      )}
                    </VariantsTab>
                  </TabContainer>
                  <CardSpacer />
                  {activeTab === VoucherDetailsPageTab.categories ? (
                    <DiscountExcludeCategories
                      disabled={disabled}
                      onCategoryAssign={onExcludeCategoryAssign}
                      onCategoryUnassign={onExcludeCategoryUnassign}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onRowClick={onCategoryClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={excludeCategoryListToolbar}
                    />
                  ) : activeTab === VoucherDetailsPageTab.collections ? (
                    <DiscountExcludeCollections
                      disabled={disabled}
                      onCollectionAssign={onExcludeCollectionAssign}
                      onCollectionUnassign={onExcludeCollectionUnassign}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onRowClick={onCollectionClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={excludeCollectionListToolbar}
                    />
                  ) : activeTab === VoucherDetailsPageTab.products ? (
                    <DiscountExcludeProducts
                      disabled={disabled}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onProductAssign={onExcludeProductAssign}
                      onProductUnassign={onExcludeProductUnassign}
                      onRowClick={onProductClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={excludeProductListToolbar}
                    />
                  ) : (
                    <DiscountExcludeVariants
                      disabled={disabled}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      onVariantAssign={onExcludeVariantAssign}
                      onVariantUnassign={onExcludeVariantUnassign}
                      onRowClick={onVariantClick}
                      pageInfo={pageInfo}
                      discount={voucher}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={excludeVariantListToolbar}
                    />
                  )}
                </>
              ) : null}
              <CardSpacer />
              {data.discountType.toString() === "SHIPPING" ? (
                <CountryList
                  countries={maybe(() => voucher.countries)}
                  disabled={disabled}
                  emptyText={intl.formatMessage({
                    defaultMessage: "Voucher applies to all countries"
                  })}
                  title={
                    <>
                      {intl.formatMessage({
                        defaultMessage: "Countries",
                        description: "voucher country range"
                      })}
                      <Typography variant="caption">
                        <FormattedMessage defaultMessage="Voucher is limited to these countries" />
                      </Typography>
                    </>
                  }
                  onCountryAssign={onCountryAssign}
                  onCountryUnassign={onCountryUnassign}
                />
              ) : null}
              <CardSpacer />
              <VoucherRequirements
                data={data}
                disabled={disabled}
                defaultCurrency={defaultCurrency}
                errors={errors}
                onChange={change}
              />
              <CardSpacer />
              <VoucherLimits
                data={data}
                disabled={disabled}
                defaultCurrency={defaultCurrency}
                errors={errors}
                onChange={change}
              />
              <CardSpacer />
              <DiscountDates
                data={data}
                disabled={disabled}
                defaultCurrency={defaultCurrency}
                errors={errors}
                onChange={change}
              />
            </div>
            <div>
              <VoucherSummary
                defaultCurrency={defaultCurrency}
                voucher={voucher}
              />
              <CardSpacer />
              <LanguageChoice
                disabled={disabled}
                isChecked={isChecked}
                selected={selected}
                toggle={toggle}
                toggleAll={toggleAll}
                toolbar={productListToolbar}
                languageList={voucher?.languages}
                onLanguageAssign={onLanguageAssign}
                onLanguageUnAssign={onLanguageUnAssign}
              />
            </div>
            {/* <div>
              <LanguageChoice
                languageList={}
              />
            </div> */}
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            onCancel={onBack}
            onDelete={onRemove}
            onSave={submit}
            state={saveButtonBarState}
          />
        </Container>
      )}
    </Form>
  );
};

export default VoucherDetailsPage;
